import React, { useState, useEffect } from 'react';
import { 
    Box, Button, TextField, Typography, Paper, Container, Stack, Alert, 
    CircularProgress, Grid, Card, CardContent, Divider 
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate, Link } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { getApiUrl } from '../../config/api';

const SchoolSignupPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [plansLoading, setPlansLoading] = useState(true);
    const [error, setError] = useState('');
    const [plans, setPlans] = useState<any[]>([]);

    // Form State
    const [formData, setFormData] = useState({
        schoolName: '',
        adminName: '',
        adminEmail: '',
        adminPassword: '',
        phone: '',
        address: '',
        planCode: 'BASIC' // Default selection
    });

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const res = await axios.get(getApiUrl('auth/plans'));
                // Filter out the hidden Trial plan from selection, as all signups get a trial of their chosen plan
                setPlans(res.data.filter((p: any) => p.code !== 'FREE_TRIAL'));
            } catch (err) {
                console.error('Failed to fetch plans');
            } finally {
                setPlansLoading(false);
            }
        };
        fetchPlans();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handlePlanSelect = (code: string) => {
        setFormData({ ...formData, planCode: code });
    };

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await axios.post(getApiUrl('auth/signup-school'), formData);
            // Registration successful, redirect to login
            navigate('/login', { state: { message: `Welcome to SquareEdge! Your 14-Day Free Trial for the ${formData.planCode} plan has been activated. Please sign in.` } });
        } catch (err: any) {
            setError(err.response?.data?.message || 'Registration failed.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#0B0F19',
                backgroundImage: 'radial-gradient(circle at 50% -20%, #1a237e 0%, #0B0F19 70%)',
                py: 6
            }}
        >
            <Box sx={{ position: 'absolute', top: 20, left: 20, zIndex: 10 }}>
                <Button 
                    startIcon={<ArrowBackIcon />} 
                    onClick={() => navigate('/')}
                    sx={{ color: 'white', textTransform: 'none', fontWeight: 600 }}
                >
                    Back to Website
                </Button>
            </Box>
            <Container maxWidth="lg">
                <Paper
                    elevation={10}
                    sx={{
                        p: { xs: 3, md: 6 },
                        borderRadius: 6,
                        bgcolor: 'rgba(255,255,255,0.05)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.1)'
                    }}
                >
                    <Stack spacing={5}>
                        <Box textAlign="center">
                            <Typography variant="h3" fontWeight={800} color="white" gutterBottom>Launch Your Digital Campus</Typography>
                            <Typography variant="h6" color="rgba(255,255,255,0.6)">Register your school and enjoy a 14-day premium trial today.</Typography>
                        </Box>

                        {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

                        <form onSubmit={handleRegister}>
                            <Grid container spacing={4}>
                                {/* Left Side: School & Admin Details */}
                                <Grid size={{ xs: 12, lg: 7 }}>
                                    <Stack spacing={4}>
                                        <Box sx={{ p: 4, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 4, border: '1px solid rgba(255,255,255,0.08)' }}>
                                            <Typography variant="h6" color="#ffab00" fontWeight="bold" mb={3}>1. School Information</Typography>
                                            <Stack spacing={3}>
                                                <TextField
                                                    name="schoolName" label="School / Institution Name" variant="outlined" fullWidth required
                                                    value={formData.schoolName} onChange={handleChange}
                                                    InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                                    InputProps={{ style: { color: 'white' }, sx: { borderRadius: 2, bgcolor: 'rgba(255,255,255,0.05)' } }}
                                                />
                                                <TextField
                                                    name="phone" label="Institution Phone Number" variant="outlined" fullWidth required
                                                    value={formData.phone} onChange={handleChange}
                                                    InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                                    InputProps={{ style: { color: 'white' }, sx: { borderRadius: 2, bgcolor: 'rgba(255,255,255,0.05)' } }}
                                                />
                                                <TextField
                                                    name="address" label="Main Campus Physical Address" variant="outlined" multiline rows={3} fullWidth required
                                                    value={formData.address} onChange={handleChange}
                                                    InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                                    InputProps={{ style: { color: 'white' }, sx: { borderRadius: 2, bgcolor: 'rgba(255,255,255,0.05)' } }}
                                                />
                                            </Stack>
                                        </Box>

                                        <Box sx={{ p: 4, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 4, border: '1px solid rgba(255,255,255,0.08)' }}>
                                            <Typography variant="h6" color="#ffab00" fontWeight="bold" mb={3}>2. Primary Administrator</Typography>
                                            <Stack spacing={3}>
                                                <TextField
                                                    name="adminName" label="Full Name" variant="outlined" fullWidth required
                                                    value={formData.adminName} onChange={handleChange}
                                                    InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                                    InputProps={{ style: { color: 'white' }, sx: { borderRadius: 2, bgcolor: 'rgba(255,255,255,0.05)' } }}
                                                />
                                                <Grid container spacing={2}>
                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                        <TextField
                                                            name="adminEmail" label="Email Address" type="email" variant="outlined" fullWidth required
                                                            value={formData.adminEmail} onChange={handleChange}
                                                            InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                                            InputProps={{ style: { color: 'white' }, sx: { borderRadius: 2, bgcolor: 'rgba(255,255,255,0.05)' } }}
                                                        />
                                                    </Grid>
                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                        <TextField
                                                            name="adminPassword" label="Create Password" type="password" variant="outlined" fullWidth required
                                                            value={formData.adminPassword} onChange={handleChange}
                                                            InputLabelProps={{ style: { color: 'rgba(255,255,255,0.7)' } }}
                                                            InputProps={{ style: { color: 'white' }, sx: { borderRadius: 2, bgcolor: 'rgba(255,255,255,0.05)' } }}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Stack>
                                        </Box>
                                    </Stack>
                                </Grid>

                                {/* Right Side: Plan Selection */}
                                <Grid size={{ xs: 12, lg: 5 }}>
                                    <Box sx={{
                                        position: 'sticky', top: 20,
                                        p: 4, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 4, border: '1px solid rgba(255,255,255,0.08)', height: '100%'
                                    }}>
                                        <Typography variant="h6" color="#ffab00" fontWeight="bold" mb={3}>3. Select Future Plan</Typography>
                                        <Typography variant="body2" color="rgba(255,255,255,0.5)" mb={3}>
                                            Choose the plan your school will transition to after your **14-day free trial**. No credit card required now.
                                        </Typography>

                                        {plansLoading ? (
                                            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
                                        ) : (
                                            <Stack spacing={2}>
                                                {plans.map((plan) => (
                                                    <Card 
                                                        key={plan.code}
                                                        onClick={() => handlePlanSelect(plan.code)}
                                                        sx={{ 
                                                            cursor: 'pointer',
                                                            bgcolor: formData.planCode === plan.code ? 'rgba(255, 171, 0, 0.15)' : 'rgba(255,255,255,0.02)',
                                                            border: formData.planCode === plan.code ? '2px solid #ffab00' : '1px solid rgba(255,255,255,0.1)',
                                                            transition: 'all 0.2s',
                                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' }
                                                        }}
                                                    >
                                                        <CardContent sx={{ p: '16px !important' }}>
                                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                                <Box>
                                                                    <Typography variant="h6" fontWeight="bold" color="white">{plan.name}</Typography>
                                                                    <Typography variant="caption" color="rgba(255,255,255,0.5)">
                                                                        Up to {plan.maxStudents} Students • {plan.maxUsers} Staff
                                                                    </Typography>
                                                                </Box>
                                                                <Box textAlign="right">
                                                                    <Typography variant="h6" fontWeight="bold" color="#ffab00">${plan.price}</Typography>
                                                                    <Typography variant="caption" color="rgba(255,255,255,0.5)">/ month</Typography>
                                                                </Box>
                                                            </Stack>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </Stack>
                                        )}

                                        <Divider sx={{ my: 4, bgcolor: 'rgba(255,255,255,0.1)' }} />

                                        <Stack spacing={2}>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                                                <Typography variant="body2" color="rgba(255,255,255,0.8)">All Premium Features Included</Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                                                <Typography variant="body2" color="rgba(255,255,255,0.8)">Zero Setup Fees</Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                                                <Typography variant="body2" color="rgba(255,255,255,0.8)">Instant Account Activation</Typography>
                                            </Stack>

                                            <Button
                                                type="submit" variant="contained" size="large" fullWidth disabled={loading}
                                                sx={{ 
                                                    mt: 3, bgcolor: '#ffab00', color: 'black', fontWeight: 800, py: 2, borderRadius: 3, fontSize: '1.1rem',
                                                    '&:hover': { bgcolor: '#ffca28', transform: 'translateY(-2px)' },
                                                    transition: 'all 0.3s'
                                                }}
                                            >
                                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Start Your Free Trial'}
                                            </Button>
                                        </Stack>
                                    </Box>
                                </Grid>
                            </Grid>
                        </form>

                        <Box textAlign="center">
                            <Typography variant="body1" color="rgba(255,255,255,0.6)">
                                Already managing a school?{' '}
                                <Link to="/login" style={{ color: '#ffab00', textDecoration: 'none', fontWeight: 'bold' }}>
                                    Sign In to Portal
                                </Link>
                            </Typography>
                        </Box>
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
};

export default SchoolSignupPage;
